import type {
  EngineAdapter,
  EngineId,
  Layer,
  LayerRenderDiagnosticsLike,
  ViewerCamera,
  ViewerEvent,
  ViewerHostContext,
} from './types';
import { routeLayer, ROUTING_ENGINE_CESIUM, ROUTING_ENGINE_GIRO3D, type EngineRoutingPolicy } from './policy/engineRouting';

export interface ViewerHostOptions {
  container: HTMLElement;
  primaryEngine: EngineId;
  adapters: EngineAdapter[];
  routingPolicy?: EngineRoutingPolicy;
}

type Listener = (e: ViewerEvent) => void;

/**
 * Engine-agnostic ViewerHost. Routes a Layer to the right adapter, fans events
 * to subscribers, and exposes camera/time/selection control.
 *
 * STUB matching `@geosoft/viewer-sdk` API draft. Replace with the published
 * package per RFC-001.
 */
export class ViewerHost {
  private opts: ViewerHostOptions;
  private mounted = false;
  private listeners = new Set<Listener>();
  private layers = new Map<string, { layer: Layer; engineId: EngineId }>();
  private currentTime: Date | null = null;
  /**
   * Mounts that have not resolved yet, keyed by layer id.
   *
   * Raster mounts take seconds (TileJSON + `/cog/info` + `/cog/statistics`), and
   * two independent call sites request them: the eye-toggle handler and the
   * layer-sync effect. Without coalescing, one show produced two concurrent
   * `addLayer` calls, and the slower one landed *after* the user's next hide —
   * re-adding the layer with default visibility, so the eye was off but the
   * raster stayed on screen.
   */
  private pendingAdds = new Map<string, Promise<void>>();
  /** Layers whose removal was requested while their mount was still in flight. */
  private removeWhilePending = new Set<string>();
  /** Last requested visibility, honoured by mounts that resolve afterwards. */
  private desiredVisible = new Map<string, boolean>();

  constructor(opts: ViewerHostOptions) {
    this.opts = opts;
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit = (e: ViewerEvent): void => {
    for (const l of this.listeners) l(e);
  };

  /**
   * Mount every adapter, primary first.
   *
   * Only the primary engine is required. A companion adapter may legitimately
   * refuse the host — MapLibre rides along with Giro3D for 2D vector overlays
   * and rejects software WebGL, which used to abort the whole 3D mount and
   * bounce the session to the Canvas 2D fallback even though Giro3D itself had
   * started. Companions that fail are logged and dropped so layer routing
   * never hands a layer to an adapter that never mounted.
   */
  async mount(): Promise<void> {
    if (this.mounted) return;
    const ctx: ViewerHostContext = {
      container: this.opts.container,
      primaryEngine: this.opts.primaryEngine,
      emit: this.emit,
    };
    const unusable = new Set<EngineAdapter>();
    for (const a of this.opts.adapters) {
      try {
        await a.mount(ctx);
      } catch (err) {
        if (a.id === this.opts.primaryEngine) throw err;
        console.warn(
          `[ViewerHost] companion adapter '${a.id}' did not mount; continuing without it`,
          err,
        );
        unusable.add(a);
      }
    }
    if (unusable.size > 0) {
      this.opts.adapters = this.opts.adapters.filter((a) => !unusable.has(a));
    }
    this.mounted = true;
  }

  async unmount(): Promise<void> {
    if (!this.mounted) return;
    for (const a of [...this.opts.adapters].reverse()) {
      await a.unmount();
    }
    this.mounted = false;
  }

  /** Pick the best engine for a layer kind: routing policy first, primary second. */
  private pickEngine(layer: Layer): EngineAdapter {
    const pointCloudAdapter = this.pickPointCloudAdapter(layer);
    if (pointCloudAdapter) return pointCloudAdapter;

    if (
      this.opts.primaryEngine === ROUTING_ENGINE_GIRO3D &&
      layer.kind === 'vector' &&
      typeof layer.endpoints.pmtiles === 'string' &&
      layer.endpoints.pmtiles.length > 0
    ) {
      const primary = this.opts.adapters.find(
        (a) => a.id === ROUTING_ENGINE_GIRO3D && a.supports(layer.kind),
      );
      if (primary) return primary;
    }

    const routed = routeLayer(layer, this.opts.routingPolicy);
    const routedAdapter = this.opts.adapters.find(
      (a) => a.id === routed && a.supports(layer.kind),
    );
    if (routedAdapter) return routedAdapter;

    const primary = this.opts.adapters.find(
      (a) => a.id === this.opts.primaryEngine && a.supports(layer.kind),
    );
    if (primary) return primary;
    const fallback = this.opts.adapters.find((a) => a.supports(layer.kind));
    if (!fallback) {
      throw new Error(`No adapter supports layer kind '${layer.kind}'`);
    }
    return fallback;
  }

  private pickPointCloudAdapter(layer: Layer): EngineAdapter | null {
    if (layer.kind !== 'pointcloud') return null;
    const prefer = (engineId: EngineId): EngineAdapter | null => (
      this.opts.adapters.find((adapter) => adapter.id === engineId && adapter.supports(layer.kind)) ?? null
    );
    if (layer.endpoints.tileset3d) {
      return prefer(ROUTING_ENGINE_GIRO3D) ?? prefer(ROUTING_ENGINE_CESIUM);
    }
    const pointcloud = layer.endpoints.pointcloud;
    const isCopc = Boolean(layer.endpoints.copc) || (typeof pointcloud === 'string' && /\.copc(?:\.laz)?(?:[?#].*)?$/i.test(pointcloud));
    if (isCopc) {
      return prefer(ROUTING_ENGINE_GIRO3D);
    }
    const isPotree = Boolean(layer.endpoints.potree) || (typeof pointcloud === 'string' && /(?:^|\/)metadata\.json(?:[?#].*)?$/i.test(pointcloud));
    if (isPotree) {
      return prefer('potree');
    }
    return null;
  }

  async addLayer(layer: Layer): Promise<void> {
    if (this.layers.has(layer.id)) return;
    const inFlight = this.pendingAdds.get(layer.id);
    if (inFlight) return inFlight;

    const engine = this.pickEngine(layer);
    const task = (async () => {
      try {
        await engine.addLayer(layer);
        this.layers.set(layer.id, { layer, engineId: engine.id });
      } catch (err) {
        // Nothing mounted, so a queued removal has nothing left to undo.
        this.removeWhilePending.delete(layer.id);
        throw err;
      } finally {
        this.pendingAdds.delete(layer.id);
      }
      // A hide or remove issued while this mount was in flight wins over it.
      if (this.removeWhilePending.delete(layer.id)) {
        await this.detach(layer.id);
        return;
      }
      if (this.desiredVisible.get(layer.id) === false) {
        if (typeof engine.setLayerVisible === 'function') {
          await engine.setLayerVisible(layer.id, false);
        } else {
          await this.detach(layer.id);
        }
      }
    })();
    this.pendingAdds.set(layer.id, task);
    return task;
  }

  /** Unmount from the owning adapter and drop the host registration. */
  private async detach(layerId: string): Promise<void> {
    const entry = this.layers.get(layerId);
    if (!entry) return;
    this.layers.delete(layerId);
    const engine = this.opts.adapters.find((a) => a.id === entry.engineId);
    if (engine) await engine.removeLayer(layerId);
  }

  /**
   * Toggle visibility on an already-mounted layer. Prefers the adapter's
   * `setLayerVisible` so 3D Tiles / point cloud entities stay alive across
   * show/hide; falls back to remove/add when the adapter lacks the hook.
   *
   * When the layer is not in the host map yet (in-flight `addLayer`), still
   * forward the request to adapters that implement `setLayerVisible` so they
   * can stamp desired visibility — otherwise a later hide is lost and the
   * completing add resurrects the layer (hide→show→hide race).
   */
  async setLayerVisible(layerId: string, visible: boolean): Promise<void> {
    this.desiredVisible.set(layerId, visible);
    const entry = this.layers.get(layerId);
    if (!entry) {
      for (const engine of this.opts.adapters) {
        if (typeof engine.setLayerVisible === 'function') {
          await engine.setLayerVisible(layerId, visible);
        }
      }
      return;
    }
    const engine = this.opts.adapters.find((a) => a.id === entry.engineId);
    if (!engine) return;
    if (typeof engine.setLayerVisible === 'function') {
      await engine.setLayerVisible(layerId, visible);
      return;
    }
    if (visible) {
      // The layer is already mounted from the host's perspective. Adapters
      // without an in-place visibility hook (MapLibre vector/PMTiles) are
      // re-shown by `addLayer` before this method is called; adding again
      // duplicates sources/layers and breaks visibility.
      return;
    } else {
      await this.detach(layerId);
    }
  }

  setBlockVisible(layerId: string, blockCode: string, visible: boolean): void {
    const entry = this.layers.get(layerId);
    if (!entry) return;
    const engine = this.opts.adapters.find((a) => a.id === entry.engineId);
    if (engine && typeof engine.setBlockVisible === 'function') {
      engine.setBlockVisible(layerId, blockCode, visible);
    }
  }

  setBlockOpacity(layerId: string, blockCode: string, opacity: number): void {
    const entry = this.layers.get(layerId);
    if (!entry) return;
    const engine = this.opts.adapters.find((a) => a.id === entry.engineId);
    if (engine && typeof engine.setBlockOpacity === 'function') {
      engine.setBlockOpacity(layerId, blockCode, opacity);
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    const pending = this.pendingAdds.get(layerId);
    if (pending) {
      // The mount has not registered yet, so there is nothing to unmount — but
      // it must not survive this request. The mount tail honours the flag.
      this.removeWhilePending.add(layerId);
      await pending;
      return;
    }
    await this.detach(layerId);
  }

  setTime(t: Date): void {
    this.currentTime = t;
    for (const a of this.opts.adapters) a.setTime?.(t);
    this.emit({ type: 'timeChange', time: t });
  }

  flyTo(camera: ViewerCamera): void {
    for (const a of this.opts.adapters) a.flyTo?.(camera);
  }

  listLayers(): Layer[] {
    return [...this.layers.values()].map((v) => v.layer);
  }

  /** Engine id currently routing a mounted layer, or `null` when unmounted. */
  getLayerEngineId(layerId: string): EngineId | null {
    return this.layers.get(layerId)?.engineId ?? null;
  }

  /**
   * Aggregate live render diagnostics for the global Layer Properties console.
   * Prefers the owning adapter (the one that mounted the layer); otherwise
   * probes every adapter so a kind that *would* route somewhere can still be
   * inspected before it mounts. Returns `null` when nothing can answer.
   *
   * Layer-kind aware via the adapters — never layer-id aware here.
   */
  getLayerDiagnostics(layerId: string): LayerRenderDiagnosticsLike | null {
    const entry = this.layers.get(layerId);
    if (entry) {
      const owner = this.opts.adapters.find((a) => a.id === entry.engineId);
      const owned = owner?.getLayerRenderDiagnostics?.(layerId);
      if (owned) return owned;
    }
    for (const adapter of this.opts.adapters) {
      const reported = adapter.getLayerRenderDiagnostics?.(layerId);
      if (reported) return reported;
    }
    return null;
  }

  /**
   * The CRS the viewer is actually rendering in (viewer-level coordinate
   * readout). Asks the primary engine first (it owns the render frame), then
   * any adapter that reports one. Returns `null` when no engine claims a frame.
   */
  getActiveFrameCrs(): string | null {
    const primary = this.opts.adapters.find((a) => a.id === this.opts.primaryEngine);
    const fromPrimary = primary?.getActiveFrameCrs?.();
    if (typeof fromPrimary === 'string' && fromPrimary.length > 0) return fromPrimary;
    for (const adapter of this.opts.adapters) {
      const reported = adapter.getActiveFrameCrs?.();
      if (typeof reported === 'string' && reported.length > 0) return reported;
    }
    return null;
  }

  getTime(): Date | null {
    return this.currentTime;
  }
}
